import { AmbientBackground, buttonVariants } from '@intelliauth/ui'
import { Link, Navigate, Route, Routes } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import RedirectIfAuthenticated from './guards/RedirectIfAuthenticated'
import RequireAuth from './guards/RequireAuth'
import LoginPage from './routes/auth/LoginPage'
import RegisterPage from './routes/auth/RegisterPage'
import TenantAdminAcceptPage from './routes/auth/TenantAdminAcceptPage'
import VerifyEmailPage from './routes/auth/VerifyEmailPage'
import VerifyEmailSentPage from './routes/auth/VerifyEmailSentPage'
import ForbiddenPage from './routes/ForbiddenPage'
import InviteAcceptPage from './routes/InviteAcceptPage'
import AuditPage from './routes/org/AuditPage'
import DashboardHome from './routes/org/DashboardHome'
import DashboardLayout from './routes/org/DashboardLayout'
import MembersPage from './routes/org/MembersPage'
import OrgIdentitiesPage from './routes/org/OrgIdentitiesPage'
import SettingsPage from './routes/org/SettingsPage'
import TenantDetailPage from './routes/org/TenantDetailPage'
import TenantsPage from './routes/org/TenantsPage'

function App() {
  return (
    <Routes>
      {/* Root → redirect to dashboard (auth guard handles /login bounce) */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* Invitation acceptance (public — works with or without auth) */}
      <Route path="/invite/:token" element={<InviteAcceptPage />} />

      {/* Email verification — public, works regardless of auth state */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/verify-email/sent" element={<VerifyEmailSentPage />} />

      {/* Tenant-admin activation — public; intentionally not wrapped in
          RedirectIfAuthenticated so a user signed in as someone else
          sees the "already activated / sign in as X" state instead of
          getting bounced to /dashboard. */}
      <Route path="/tenant-admin/accept" element={<TenantAdminAcceptPage />} />

      {/* Auth routes — redirect to dashboard if already logged in */}
      <Route element={<RedirectIfAuthenticated />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
      </Route>

      {/* Dashboard routes — require authentication */}
      <Route path="/dashboard" element={<RequireAuth />}>
        <Route element={<DashboardLayout />}>
          <Route index element={<DashboardHome />} />
          <Route path="tenants" element={<TenantsPage />} />
          <Route path="tenants/:tenantId" element={<TenantDetailPage />} />
          <Route path="identities" element={<OrgIdentitiesPage />} />
          <Route path="members" element={<MembersPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="billing" element={<PlaceholderPage title="Billing" />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>
      </Route>

      {/* Error pages */}
      <Route path="/forbidden" element={<ForbiddenPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}

function PlaceholderPage({ title }: { title: string }) {
  return (
    <div>
      <h1 className="text-2xl font-semibold text-foreground tracking-tight">{title}</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        This page will be built in a future sprint.
      </p>
      <div className="mt-8 flex items-center justify-center border-2 border-dashed border-border rounded-lg p-16">
        <p className="text-muted-foreground/70 text-sm">Coming soon</p>
      </div>
    </div>
  )
}

function NotFoundPage() {
  const { user } = useAuth()
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <AmbientBackground variant="full" />
      <div className="text-center animate-fade-in">
        <h1 className="text-6xl font-bold text-foreground">404</h1>
        <p className="mt-4 text-lg text-muted-foreground">Page not found</p>
        <Link
          to={user ? '/dashboard' : '/login'}
          className={`mt-6 ${buttonVariants({ variant: 'primary' })}`}
        >
          Go back home
        </Link>
      </div>
    </div>
  )
}

export default App
